import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import AppWindow from "@/components/system/AppWindow";
import { useWindows } from "@/hooks/useWindows";
import { APPS } from "@/data/apps";
import { RoleBasedApp } from "@/components/common/RoleBasedApp";

// Lazy-loaded App-Komponenten - werden erst beim ersten Oeffnen geladen
const SettingsApp = lazy(() => import("@/components/applications/SettingsApp"));
const AppStoreApp = lazy(() => import("@/components/applications/AppStoreApp"));
const FilePreviewApp = lazy(() => import("@/components/applications/file-preview/FilePreviewApp"));
const Drive2App = lazy(() => import("@/components/applications/drive2/Drive2App"));
const Communicator2App = lazy(() => import("@/components/applications/communicator2/Communicator2App"));
const CollaboraEditorWindow = lazy(() => import("@/components/applications/communicator2/components/CollaboraEditorWindow"));
const AIKnowledgeExtractorApp = lazy(() => import("@/components/applications/AIKnowledgeExtractorApp"));
const KnowledgeVerifierApp = lazy(() => import("@/components/applications/KnowledgeVerifierApp"));
const PromptsPanelRedirect = lazy(() => import("@/components/widgets/bar/PromptsPanelRedirect").then((m) => ({ default: m.PromptsPanelRedirect })));
const SessionsApp = lazy(() => import("@/components/applications/SessionsApp"));
const TrainingApp = lazy(() => import("@/components/applications/TrainingApp"));
const WidgetBarEditRedirect = lazy(() => import("@/components/widgets/bar/WidgetBarEditRedirect").then((m) => ({ default: m.WidgetBarEditRedirect })));
const WissenshausApp = lazy(() => import("@/components/applications/wissenshaus/WissenshausApp"));
const HarnessApp = lazy(() => import("@/components/applications/harness/HarnessApp"));
const ControlApp = lazy(() => import("@/components/applications/control/ControlApp"));
const AdminPanelApp = lazy(() => import("@/components/applications/AdminPanelApp").then(m => ({ default: m.AdminPanelApp })));
const ExternalAppWindow = lazy(() => import("@/components/applications/external-app/ExternalAppWindow").then(m => ({ default: m.ExternalAppWindow })));
import { SnapDivider } from "@/components/system/SnapDivider";
import { TASKBAR_HEIGHT, MIN_WINDOW_HEIGHT } from "@/shared/ui";
import { resolveWindowAnimationContext } from "@/utils/windowAnimations";
import { WindowContext, type WindowAppProps } from "@/types/window";
import { WindowCloseGuardDialog } from "./WindowCloseGuardDialog";
import { WindowAppHost } from "@/components/system/WindowAppHost";
import { windowAppIcon } from "@/lib/windowAppIcon";

/** Minimaler Fallback waehrend eine lazy-geladene App-Komponente geladen wird */
function LazyFallback() {
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin opacity-40" />
    </div>
  );
}

type WindowContentCache = Map<string, { component: ReactNode; appId: string }>;

/**
 * Wrapper component that provides WindowContext and memoizes to prevent re-renders
 * Uses a custom comparison to avoid re-renders when params content is the same
 */
const WindowContentWrapper = memo(
  ({
    windowId,
    params,
    onClose,
    children,
  }: WindowAppProps & { children: ReactNode }) => {
    const windowProps: WindowAppProps = useMemo(
      () => ({
        windowId,
        onClose,
        params,
      }),
      [windowId, onClose, params]
    );

    return (
      <WindowContext.Provider value={windowProps}>
        <WindowAppHost>{children}</WindowAppHost>
      </WindowContext.Provider>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if content actually changed
    if (prevProps.windowId !== nextProps.windowId) return false;
    if (prevProps.onClose !== nextProps.onClose) return false;
    if (prevProps.children !== nextProps.children) return false;

    // Deep compare params
    const prevParams = JSON.stringify(prevProps.params || {});
    const nextParams = JSON.stringify(nextProps.params || {});
    if (prevParams !== nextParams) return false;

    return true; // No re-render needed
  }
);
WindowContentWrapper.displayName = "WindowContentWrapper";

/**
 * WindowContainer
 * - Rendert alle offenen Fenster basierend auf useWindows
 * - Minimal & fokussiert: nur Rendering/Events der AppWindow-Komponenten
 * - Behält App-Components mounted um State und Scroll-Position zu erhalten
 */
export default function WindowContainer() {
  const {
    windows,
    activeId,
    closeWindow,
    requestWindowClose,
    toggleMinimize,
    focusWindow,
    updateWindowPosition,
  } = useWindows();
  const { t } = useTranslation();
  const contentCacheRef = useRef<WindowContentCache>(new Map());
  const [everOpenedWindows, setEverOpenedWindows] = useState<Set<string>>(
    new Set()
  );

  // Track initial createdAt for stable z-order (doesn't change on focus)
  const initialCreatedAtRef = useRef<Map<string, number>>(new Map());

  // Create stable callbacks for each window to prevent unnecessary re-renders
  const windowCallbacksRef = useRef<
    Map<
      string,
      {
        onClose: () => void;
        onCloseRequest: () => boolean;
        onToggleMinimize: () => void;
        onFocus: () => void;
        onPositionChange: (pos: any, options?: any) => void;
      }
    >
  >(new Map());

  const getWindowCallbacks = useCallback(
    (windowId: string) => {
      if (!windowCallbacksRef.current.has(windowId)) {
        windowCallbacksRef.current.set(windowId, {
          onClose: () => closeWindow(windowId),
          // Vorabfrage fuer die Schliessanimation — siehe `requestWindowClose`.
          onCloseRequest: () => requestWindowClose(windowId),
          onToggleMinimize: () => toggleMinimize(windowId),
          onFocus: () => focusWindow(windowId),
          onPositionChange: (pos: any, options?: any) =>
            updateWindowPosition(windowId, pos, options),
        });
      }
      return windowCallbacksRef.current.get(windowId)!;
    },
    [closeWindow, requestWindowClose, toggleMinimize, focusWindow, updateWindowPosition]
  );

  // Separate stable callback for window content wrapper
  const onCloseCallbacks = useRef<Map<string, () => void>>(new Map());
  const getOnCloseCallback = useCallback(
    (windowId: string) => {
      if (!onCloseCallbacks.current.has(windowId)) {
        onCloseCallbacks.current.set(windowId, () => closeWindow(windowId));
      }
      return onCloseCallbacks.current.get(windowId)!;
    },
    [closeWindow]
  );

  // Track all windows that have ever been opened (for keeping them mounted)
  // Also track their INITIAL createdAt for stable z-order
  useEffect(() => {
    setEverOpenedWindows((prev) => {
      const updated = new Set(prev);
      windows.forEach((w) => {
        updated.add(w.id);
        // Store initial createdAt only once when window is first opened
        if (!initialCreatedAtRef.current.has(w.id)) {
          initialCreatedAtRef.current.set(w.id, w.createdAt);
        }
      });
      return updated;
    });
  }, [windows]);

  // Clean up cache for windows that have been closed and are no longer in everOpenedWindows
  useEffect(() => {
    const currentIds = new Set(windows.map((window) => window.id));
    for (const cacheKey of contentCacheRef.current.keys()) {
      if (!currentIds.has(cacheKey)) {
        // Keep in cache but mark for cleanup later
        // We'll actually remove it after a delay to allow smooth closing animations
        setTimeout(() => {
          if (!windows.find((w) => w.id === cacheKey)) {
            contentCacheRef.current.delete(cacheKey);
            initialCreatedAtRef.current.delete(cacheKey); // Clean up initial createdAt
            setEverOpenedWindows((prev) => {
              const updated = new Set(prev);
              updated.delete(cacheKey);
              return updated;
            });
          }
        }, 1000);
      }
    }
  }, [windows]);

  const getWindowContent = useCallback(
    (windowId: string, appId: string): ReactNode => {
      const cached = contentCacheRef.current.get(windowId);
      if (cached && cached.appId === appId) return cached.component;

      let content: React.ReactNode;

      switch (appId) {
        case "settings":
          content = <SettingsApp />;
          break;
        case "app-store":
          content = <AppStoreApp />;
          break;
        case "file-preview":
          content = (
            <RoleBasedApp
              appName="file-preview"
              appDisplayName={t('apps.file-preview')}
              appDescription={t('apps.file-preview-description')}
            >
              <FilePreviewApp />
            </RoleBasedApp>
          );
          break;
        case "drive2":
          content = (
            <RoleBasedApp
              appName="drive2"
              appDisplayName={t('apps.drive2')}
              appDescription={t('apps.drive2-description')}
            >
              <Drive2App />
            </RoleBasedApp>
          );
          break;
        case "communicator2":
          content = <Communicator2App />;
          break;
        case "wissenshaus":
          content = <WissenshausApp />;
          break;
        case "harness":
          content = <HarnessApp />;
          break;
        case "cdb-control":
          content = <ControlApp />;
          break;
        case "collabora-editor":
          content = <CollaboraEditorWindow />;
          break;
        case "ai-knowledge-extractor":
          content = <AIKnowledgeExtractorApp />;
          break;
        case "knowledge-verifier":
          content = <KnowledgeVerifierApp />;
          break;
        case "prompt-library":
          content = <PromptsPanelRedirect windowId={windowId} />;
          break;
        case "cdb-sessions":
          content = <SessionsApp />;
          break;
        case "cdb-training":
          content = (
            <RoleBasedApp
              appName="cdb-training"
              appDisplayName={t('apps.cdb-training')}
              appDescription={t('apps.cdb-training-description')}
            >
              <TrainingApp />
            </RoleBasedApp>
          );
          break;
        case "widget-settings":
          content = <WidgetBarEditRedirect windowId={windowId} />;
          break;
        case "cortex":
          content = (
            <RoleBasedApp
              appName="cortex"
              appDisplayName={t('apps.cortex')}
              appDescription={t('apps.cortex-description')}
            >
              <AdminPanelApp />
            </RoleBasedApp>
          );
          break;
        default:
          // Admin-defined URL apps in window mode have no case of their own (#1505).
          content = (
            <ExternalAppWindow
              appId={appId}
              fallback={
                <div style={{ minHeight: 160 }} className="p-4">
                  <h3 className="text-lg font-semibold mb-2">{t('apps.fallbackTitle', { id: appId })}</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('apps.inDevelopment')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    {t('apps.fallbackId', { id: appId })}
                  </p>
                </div>
              }
            />
          );
          break;
      }

      // Wrap in Suspense for lazy-loaded components, then cache
      const wrapped = <Suspense fallback={<LazyFallback />}>{content}</Suspense>;
      contentCacheRef.current.set(windowId, { component: wrapped, appId });
      return wrapped;
    },
    [closeWindow, t]
  );

  // Anzeige-Reihenfolge: älteste zuerst (stabile Z-Order)
  const ordered = useMemo(
    () => [...windows].sort((a, b) => a.createdAt - b.createdAt),
    [windows]
  );
  const snappedLeft = ordered.find((w) => w.snapSide === "left");
  const snappedRight = ordered.find((w) => w.snapSide === "right");

  // Create map of current windows for quick lookup
  const currentWindowsMap = useMemo(
    () => new Map(windows.map((w) => [w.id, w])),
    [windows]
  );

  // Sort windows by INITIAL createdAt for stable DOM order
  // Z-order is now managed via CSS z-index, not DOM order
  const sortedWindowIds = useMemo(() => {
    return Array.from(everOpenedWindows)
      .map((id) => {
        const w = currentWindowsMap.get(id);
        if (!w) return null;
        // Use INITIAL createdAt for stable sort order
        const initialCreatedAt = initialCreatedAtRef.current.get(id) ?? w.createdAt;
        return {
          id: w.id,
          sortKey: initialCreatedAt,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => a.sortKey - b.sortKey)
      .map((item) => item.id);
  }, [everOpenedWindows, currentWindowsMap]);

  return (
    <>
      {sortedWindowIds.map((windowId) => {
        // Check if this window is currently open
        const w = currentWindowsMap.get(windowId);

        // If window is closed but was ever opened, skip rendering (it will be cleaned up after delay)
        if (!w) return null;

        const app = APPS.find((a) => a.id === w.appId);
        const Icon = windowAppIcon(w.appId);
        const iconElement = Icon ? <Icon className="w-4 h-4" /> : undefined;
        const animationContext = resolveWindowAnimationContext(
          w.appId,
          app?.category
        );

        // Format window title with subtitle if available
        const displayTitle = w.subtitle
          ? `${w.title} - ${w.subtitle}`
          : w.title;

        // Get content for this window (cached)
        const content = getWindowContent(w.id, w.appId);

        // Get stable callbacks for this window
        const callbacks = getWindowCallbacks(w.id);

        return (
          <AppWindow
            key={w.id}
            title={displayTitle}
            appId={w.appId}
            isActive={activeId === w.id}
            initialPosition={w.position}
            position={w.position}
            size={w.size}
            isMaximized={w.isMaximized}
            snapSide={w.snapSide}
            minimized={w.minimized}
            zIndex={w.zIndex}
            onToggleMinimize={callbacks.onToggleMinimize}
            onClose={callbacks.onClose}
            onCloseRequest={callbacks.onCloseRequest}
            onFocus={callbacks.onFocus}
            icon={iconElement}
            onPositionChange={callbacks.onPositionChange}
            animationContext={animationContext}
          >
            <WindowContentWrapper
              windowId={w.id}
              onClose={getOnCloseCallback(w.id)}
              params={w.params}
            >
              {content}
            </WindowContentWrapper>
          </AppWindow>
        );
      })}
      {snappedLeft && snappedRight && (() => {
        // Calculate divider z-index: above snapped windows, but below all other windows
        const nonSnappedWindows = windows.filter(w => w.snapSide !== 'left' && w.snapSide !== 'right')
        const maxSnappedZIndex = Math.max(snappedLeft.zIndex, snappedRight.zIndex)

        // If there are other windows, divider must be below the lowest of them
        const dividerZIndex = nonSnappedWindows.length > 0
          ? Math.min(maxSnappedZIndex + 1, Math.min(...nonSnappedWindows.map(w => w.zIndex)) - 1)
          : maxSnappedZIndex + 1

        return (
          <SnapDivider
            leftWindow={snappedLeft}
            dividerZIndex={dividerZIndex}
            onResize={(leftWidth, rightWidth) => {
            if (typeof window === "undefined") return;
            const height = Math.max(
              MIN_WINDOW_HEIGHT,
              window.innerHeight - TASKBAR_HEIGHT
            );
            updateWindowPosition(
              snappedLeft.id,
              { x: 0, y: 0 },
              {
                fromUser: true,
                size: { w: leftWidth, h: height },
                snap: "left",
              }
            );
            updateWindowPosition(
              snappedRight.id,
              { x: leftWidth, y: 0 },
              {
                fromUser: true,
                size: { w: rightWidth, h: height },
                snap: "right",
              }
            );
          }}
          />
        )
      })()}
      <WindowCloseGuardDialog />
    </>
  );
}
